import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const Terms = () => {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Terms of Service
          </h1>
        </div>

        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing and using Bookd ("the Platform"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Bookd is a marketplace platform that connects creative professionals ("Bookees") with 
              individuals and businesses seeking their services ("Bookers"). We facilitate bookings, 
              payments, and communication between parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">3. User Accounts</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You must be at least 18 years old to create an account</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must provide accurate and complete information during registration</li>
              <li>You may not use another person's account without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">4. Platform Fees</h2>
            <p className="text-muted-foreground">
              Bookd charges a service fee of 10% on all bookings. This fee is added to the service 
              price and paid by the Booker. Bookees receive the full service price minus any payment 
              processing fees. Payment is held until after the service date is completed.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">5. Bookings and Payments</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>All payments are processed through secure third-party payment providers (Stripe, PayPal)</li>
              <li>Bookees can set flat rate, hourly rate, or custom pricing</li>
              <li>Price changes require approval from both parties</li>
              <li>Full payment is charged at booking; Bookee payout is held until service completion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">6. Cancellation Policy</h2>
            <p className="text-muted-foreground">
              Cancellation penalties apply to both Bookers and Bookees. Bookees select their cancellation policy:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Flexible:</strong> Full refund up to 24 hours before start time</li>
              <li><strong>Moderate:</strong> Full refund up to 5 days before. 50% refund up to 24 hours before.</li>
              <li><strong>Strict:</strong> 50% refund up to 7 days before. No refund within 7 days.</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              <strong>Important:</strong> When using Debit Card or Apple Pay, an additional deposit for 
              the booking amount will be charged for cancellations or changes not agreed upon by both parties.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">7. Insurance</h2>
            <p className="text-muted-foreground">
              Optional booking insurance (5% of service price) is available for additional protection 
              against cancellations, damages, and other issues. Bookees are encouraged to obtain their 
              own professional liability insurance to make their profiles more marketable. Insured 
              status is displayed on Bookee profiles.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">8. User Conduct</h2>
            <p className="text-muted-foreground">Users agree not to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Post false, misleading, or fraudulent content</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Circumvent the platform's payment system</li>
              <li>Use the platform for illegal activities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">9. Content and Intellectual Property</h2>
            <p className="text-muted-foreground">
              Users retain ownership of content they upload. By posting content, you grant Bookd 
              a non-exclusive license to display and promote your content on the platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">10. Platform Intellectual Property & Prohibited Activities</h2>
            <p className="text-muted-foreground mb-3">
              The Bookd platform, including but not limited to its software, design, features, functionality, 
              and all related intellectual property, is owned exclusively by Bookd and its licensors. 
              By using this platform, you agree that you will NOT:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Reverse engineer, decompile, or disassemble</strong> any part of the Bookd platform, software, or services</li>
              <li><strong>Copy, reproduce, or clone</strong> the platform's design, features, functionality, or user interface</li>
              <li><strong>Scrape, crawl, or extract data</strong> from the platform for commercial purposes without written permission</li>
              <li><strong>Create derivative works</strong> based on the platform's code, design, or functionality</li>
              <li><strong>Remove or alter</strong> any copyright, trademark, or proprietary notices</li>
              <li><strong>Use automated tools</strong> to access, copy, or monitor the platform without authorization</li>
              <li><strong>Attempt to gain unauthorized access</strong> to our systems, servers, or databases</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              <strong>Legal Action:</strong> Violation of these terms may result in immediate account termination 
              and legal action, including claims for damages, injunctive relief, and recovery of legal fees.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">11. Contract Generation Disclaimer</h2>
            <p className="text-muted-foreground mb-3">
              Bookd provides an optional contract generation tool as a convenience to Bookers and Bookees. 
              By using this feature, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Contracts are between Booker and Bookee only.</strong> Any contract generated through Bookd is a direct agreement between the Booker (client) and the Bookee (talent). Bookd is NOT a party to any such contract.</li>
              <li><strong>Bookd provides no legal advice.</strong> The contract templates are provided for convenience only and do not constitute legal advice. We recommend consulting with a qualified attorney for important agreements.</li>
              <li><strong>No guarantee of enforceability.</strong> Bookd makes no representations or warranties regarding the legal enforceability of generated contracts in your jurisdiction.</li>
              <li><strong>User responsibility.</strong> Users are solely responsible for reviewing, understanding, and agreeing to the terms of any contract they enter into.</li>
              <li><strong>Limitation of liability.</strong> Bookd shall not be held liable for any disputes, losses, or damages arising from contracts generated through the platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">12. Booking Insurance Disclaimer</h2>
            <p className="text-muted-foreground mb-3">
              Bookd offers an optional booking insurance feature at checkout. By purchasing or declining this insurance, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Third-party provider.</strong> Booking insurance is provided by a third-party insurance provider, not by Bookd. Insurance terms, coverage, and claims are governed solely by the insurance provider's policies.</li>
              <li><strong>Bookd is not an insurer.</strong> Bookd acts only as a facilitator to offer insurance options. We are not an insurance company and do not underwrite, guarantee, or administer any insurance policy.</li>
              <li><strong>Coverage limitations.</strong> Insurance coverage is subject to the terms, conditions, and exclusions set by the insurance provider. Users should review the full policy details before purchasing.</li>
              <li><strong>Claims process.</strong> Any claims must be filed directly with the insurance provider according to their claims process. Bookd cannot process or guarantee insurance claims.</li>
              <li><strong>Not a substitute for professional insurance.</strong> Booking insurance is supplemental and should not replace comprehensive business or event insurance. Bookees should maintain their own professional liability insurance.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">13. Recommended Insurance Partners</h2>
            <p className="text-muted-foreground mb-3">
              Bookd may display links to third-party insurance providers (such as Flip Insurance and Thimble) as recommendations for freelancers and gig workers. By viewing or clicking these links, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>No affiliation.</strong> Bookd is NOT affiliated with, endorsed by, or partnered with any recommended insurance provider. We do not receive commissions or referral fees from these providers.</li>
              <li><strong>Recommendation only.</strong> Links to insurance providers are suggestions only. Users are free to choose any insurance provider of their choice.</li>
              <li><strong>No liability.</strong> Bookd assumes no responsibility or liability for insurance coverage, claims, disputes, policy terms, or any interactions with third-party insurance providers.</li>
              <li><strong>User responsibility.</strong> Users are solely responsible for reviewing and understanding all terms, conditions, coverage limits, and exclusions before purchasing any insurance policy.</li>
              <li><strong>Independent decision.</strong> The decision to purchase insurance is entirely voluntary and at the user's sole discretion.</li>
              <li><strong>Insurance badge.</strong> Users who obtain insurance coverage may display an "Insured" badge on their profile. This badge indicates the user has self-reported having insurance; Bookd does not verify insurance coverage.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">14. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              Bookd is not liable for disputes between Bookees and Bookers. We do not guarantee 
              the quality of services provided by Bookees. Users engage with each other at their own risk.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">15. External Links</h2>
            <p className="text-muted-foreground">
              Bookee profiles may contain links to external platforms (Instagram, TikTok, Discord, etc.). 
              These links will direct you off the Bookd platform. Bookd is not affiliated with and 
              is not responsible for content on external platforms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">16. Termination</h2>
            <p className="text-muted-foreground">
              We reserve the right to suspend or terminate accounts that violate these terms. 
              Users may delete their accounts at any time through account settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">17. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We may update these terms from time to time. Continued use of the platform after 
              changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">17. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms of Service shall be governed by and construed in accordance with the laws 
              of the State of New York, United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mt-8 mb-4">18. Contact</h2>
            <p className="text-muted-foreground">
              For questions about these Terms of Service, please contact us at support@alwaysbookd.com 
              or through the platform's support channels.
            </p>
          </section>
        </div>

        {/* Copyright Notice */}
        <div className="mt-12 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground text-center mb-6">
            © {new Date().getFullYear()} Bookd. All rights reserved. The Bookd name, logo, and all related 
            marks are trademarks of Bookd. Unauthorized reproduction, copying, or redistribution of this 
            platform or its contents is strictly prohibited.
          </p>
          <div className="flex justify-center">
            <Link to="/privacy">
              <Button variant="outline" className="rounded-full">
                View Privacy Policy
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
